import { useEffect, useState } from "react";
import { Loader2, Search } from "lucide-react";
import api from "@/lib/api";
import { inp, lbl } from "@/components/attendance/ui";

export default function HymnSearch({ meta, prefs, onPrefs, onPick, activeId }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const denoms = meta.denominations;
  const denom = denoms.find((d) => d.id === prefs.denomination_id) || denoms.find((d) => d.is_default);

  useEffect(() => {
    if (!q.trim()) { setRes(null); return undefined; }
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await api.get("/projection/search", { params: { q, language: prefs.language, denomination_id: denom?.id, edition_id: prefs.edition_id || "" } });
        setRes(r.data.results);
      } catch { setRes([]); } finally { setBusy(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, prefs.language, denom?.id, prefs.edition_id]);

  return (
    <div className="space-y-3" data-testid="hymn-search">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={lbl}>Numbering</label>
          <select data-testid="search-denomination" className={inp} value={denom?.id || ""} onChange={(e) => onPrefs({ ...prefs, denomination_id: e.target.value, edition_id: "" })}>
            {denoms.map((d) => <option key={d.id} value={d.id}>{d.code}{d.is_default ? " (default)" : ""}</option>)}
          </select>
        </div>
        <div>
          <label className={lbl}>Language</label>
          <div className="flex rounded border border-white/15 overflow-hidden">
            {Object.entries(meta.languages).map(([k, l]) => (
              <button key={k} data-testid={`search-lang-${k}`} onClick={() => onPrefs({ ...prefs, language: k })}
                className={`flex-1 py-2.5 text-xs tracking-wider ${prefs.language === k ? "bg-[#D4AF37] text-black font-semibold" : "text-neutral-300 hover:text-white"}`}>{l}</button>
            ))}
          </div>
        </div>
      </div>
      {denom?.editions?.length > 0 && (
        <select data-testid="search-edition" className={inp} value={prefs.edition_id || ""} onChange={(e) => onPrefs({ ...prefs, edition_id: e.target.value })}>
          <option value="">Main edition</option>
          {denom.editions.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
      )}
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
        <input data-testid="search-input" autoFocus className={`${inp} pl-9`} value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={`${denom?.code || "CAC"} number, title or first line`} />
        {busy && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[#D4AF37]" />}
      </div>
      <div className="space-y-1 max-h-[46vh] overflow-y-auto pr-1" data-testid="search-results">
        {res && !res.length && <p className="text-xs text-neutral-500 px-1 py-3" data-testid="search-empty">No hymns found.</p>}
        {res?.map((h) => (
          <button key={h.id} onClick={() => onPick(h.id)} data-testid={`search-result-${h.id}`}
            className={`w-full text-left rounded-lg px-3 py-2.5 border transition-colors ${activeId === h.id ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/5 hover:border-white/20 bg-white/[0.02]"}`}>
            <div className="flex items-baseline gap-2">
              <span className="font-serif-display text-xl text-[#D4AF37] w-14 shrink-0">{h.number}</span>
              <span className="text-sm text-white truncate">{h.title}</span>
            </div>
            <div className="text-[11px] text-neutral-500 truncate pl-16">{h.first_line}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
